import React, { useMemo } from 'react'
import { useQueries } from '@tanstack/react-query'

import type { CertificateAdmin } from 'types'
import type { CertificateGroupData } from 'hooks/api'
import { createApiCall } from 'services'

import { Stack, Typography, Box } from '@mui/material'
import { useTheme } from '@mui/material/styles'

import { Modal, Button, Alert } from '@repo/ui'
import { CertificatePreview } from 'components'
import { InfoIcon } from 'lucide-react'

interface BulkActivationModalProps {
  certificates: CertificateAdmin[]
  totalCount: number
  open: boolean
  onClose: () => void
  onConfirm: () => void
  isLoading: boolean
}

// VRG-231 gap 3 — activating an INDIVISIBLE group's PARENT also cascades
// activation to every one of its children (see ActivateCertificateUseCase),
// so a bulk selection that includes one or more indivisible parents silently
// activates MORE certificates than `totalCount` shows. This modal fetches
// each selected indivisible parent's real child count (via the same
// /certificates/admin/groups/:id endpoint the group detail panel uses, so
// the react-query cache is shared) and warns with the actual number — never
// a generic "this may activate more than shown" sentence, since the count
// is knowable and the whole point of the warning is that it's precise.
// VRG-231 review round 6 — SUPERSEDES `isIndivisibleGroupParent`, which is now a
// live under-report rather than merely narrow. The cascade used to fire only when
// the PARENT was activated, so counting parents was counting cascades. It now fires
// from ANY member of an indivisible group (activating a component activates the
// unit, so it meters once instead of N times — see ActivateCertificateUseCase), and
// a selection containing only a CHILD would have warned about nothing while taking
// the whole group live.
export const isIndivisibleGroupMember = (certificate: CertificateAdmin) =>
  certificate.group_divisibility === 'indivisible'

/**
 * The id that identifies a certificate's GROUP, from either side: a parent is its
 * own group, a child's group is its parent.
 *
 * This is what makes the count dedupe. Two selected members of the same indivisible
 * group are ONE cascade, not two — counting per certificate would double it, and
 * would do so precisely in the case a bulk activate makes easy to reach (select a
 * whole expanded group and hit Activate).
 */
export const groupIdOf = (certificate: CertificateAdmin): string =>
  certificate.parent_certificate_id ?? certificate.id

// Pure so it's independently checkable (and unit-testable the moment this
// app has a test runner — see the implementation notes for why none exists
// today) without mounting the modal or mocking react-query.
export const getBulkActivationCascadeWarning = (params: {
  /** Distinct indivisible GROUPS touched by the selection, not certificates. */
  affectedGroupCount: number
  /** Members that will activate but are NOT in the selection, deduped across groups. */
  additionalMemberCount: number
  isLoadingChildCounts: boolean
}): string | null => {
  const { affectedGroupCount, additionalMemberCount, isLoadingChildCounts } = params

  if (affectedGroupCount === 0) return null

  if (isLoadingChildCounts) {
    return 'Checking how many additional certificates this will activate.'
  }

  // The only number worth showing is the one the count above is WRONG by. If the
  // selection already contains every member of every affected group, the cascade
  // adds nothing and there is nothing to warn about — a warning there would be
  // noise, and noise is how people learn to ignore the one that mattered.
  if (additionalMemberCount === 0) return null

  const groupNoun = affectedGroupCount === 1 ? 'group' : 'groups'
  const memberNoun = additionalMemberCount === 1 ? 'certificate' : 'certificates'
  return `Your selection includes ${affectedGroupCount} indivisible ${groupNoun}. Activating any member activates the whole group, so ${additionalMemberCount} further ${memberNoun} will also go live. They are not in the count above.`
}

export const BulkActivationModal = (props: BulkActivationModalProps) => {
  const { certificates, totalCount, open, onClose, onConfirm, isLoading } = props
  const theme = useTheme()

  const nonActivatable = certificates.filter(
    c => c.status !== 'inactive' && c.status !== 'created'
  )
  const hasNonActivatable = nonActivatable.length > 0

  const visibleCount = certificates.length
  const hiddenCount = Math.max(0, totalCount - visibleCount)

  // Deduped by GROUP, not by certificate — selecting two members of one indivisible
  // group is one cascade. Keyed on the group id so the query key is identical
  // whichever member was selected, which also keeps the react-query cache warm
  // across the parent's own detail panel.
  const affectedGroupIds = useMemo(
    () => Array.from(new Set(certificates.filter(isIndivisibleGroupMember).map(groupIdOf))),
    [certificates],
  )

  const groupQueries = useQueries({
    queries: affectedGroupIds.map(certificateId => ({
      // Same query key as useCertificateGroup — reuses a warm cache if the
      // group's detail panel was already expanded on the list, and vice versa.
      queryKey: ['certificates', 'group', certificateId],
      enabled: open,
      queryFn: async () => {
        const response = await createApiCall({
          method: 'GET',
          url: `/certificates/admin/groups/${certificateId}`,
        })
        if (response.status !== 200) {
          throw new Error(`Failed to fetch certificate group ${certificateId}`)
        }
        return response.data as CertificateGroupData
      },
    })),
  })

  const isLoadingChildCounts = affectedGroupIds.length > 0 && groupQueries.some(q => q.isLoading)

  // ADDITIONAL means "will activate and is not already counted above" — the whole
  // point of the number. The old version summed every child of every parent, which
  // was right only while a child could not itself be selected. It can now, so
  // summing children would count a selected member twice: once in `totalCount` and
  // again as a surprise. Take the union of all affected groups' members and
  // subtract what the user already selected.
  const additionalMemberCount = useMemo(() => {
    const selectedIds = new Set(certificates.map(c => c.id))
    const cascadedIds = new Set<string>()
    for (const query of groupQueries) {
      const group = query.data
      if (!group) continue
      if (group.parent) cascadedIds.add(group.parent.id)
      for (const child of group.children) cascadedIds.add(child.id)
    }
    let extra = 0
    cascadedIds.forEach(id => {
      if (!selectedIds.has(id)) extra += 1
    })
    return extra
  }, [certificates, groupQueries])

  const cascadeWarning = getBulkActivationCascadeWarning({
    affectedGroupCount: affectedGroupIds.length,
    additionalMemberCount,
    isLoadingChildCounts,
  })

  return (
    <Modal
      title={`Activate ${totalCount} Certificate${totalCount > 1 ? 's' : ''}`}
      open={open}
      onClose={onClose}
      actions={
        <>
          <Button
            disableElevation
            variant="secondary"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>

          <Button
            disableElevation
            onClick={onConfirm}
            disabled={isLoading}
            loading={isLoading}
          >
            {isLoading ? 'Activating...' : `Activate ${totalCount} Certificate${totalCount > 1 ? 's' : ''}`}
          </Button>
        </>
      }
    >
      <Stack spacing={4}>
        <Alert
          severity="warning"
          variant="outlined"
          description="Activating these certificates will incur billing costs and make them immediately available to everyone who has access."
        />

        {cascadeWarning && (
          <Box sx={{
            p: 3,
            backgroundColor: theme.palette.warning[50],
            border: `1px solid ${theme.palette.warning[200]}`,
            borderRadius: 2,
          }}>
            <Typography variant="body2" color="warning.main" sx={{ lineHeight: 1.5 }}>
              {cascadeWarning}
            </Typography>
          </Box>
        )}

        {hasNonActivatable && (
          <Box sx={{
            p: 3,
            backgroundColor: theme.palette.warning[50],
            border: `1px solid ${theme.palette.warning[200]}`,
            borderRadius: 2,
          }}>
            <Typography variant="body2" color="warning.main" sx={{ mb: 2, lineHeight: 1.5 }}>
              {nonActivatable.length} certificate{nonActivatable.length > 1 ? 's' : ''} on this page cannot be activated. Only inactive or created certificates are eligible.
            </Typography>
            {nonActivatable.slice(0, 3).map((certificate) => (
              <Typography key={certificate.id} variant="body3" color="warning.main">
                {certificate.product?.title} ({certificate.status})
              </Typography>
            ))}
            {nonActivatable.length > 3 && (
              <Typography variant="body3" color="warning.main">
                ... and {nonActivatable.length - 3} more on this page
              </Typography>
            )}
          </Box>
        )}

        <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
          <Stack spacing={2}>
            {certificates.slice(0, 5).map((certificate) => (
              <CertificatePreview key={certificate.id} certificate={certificate as any} size="medium" />
            ))}
            {(visibleCount > 5 || hiddenCount > 0) && (
              <Typography variant="body3" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                {visibleCount > 5 && `... and ${visibleCount - 5} more on this page`}
                {visibleCount > 5 && hiddenCount > 0 && ' · '}
                {hiddenCount > 0 && `+ ${hiddenCount} on other page${hiddenCount > 1 ? 's' : ''}`}
              </Typography>
            )}
          </Stack>
        </Box>
      </Stack>
    </Modal>
  )
}
